import { cn } from '../../../../lib/utils';
import { ReactNode } from 'react';
import LoadingSpinner from '../../feedback-and-status/loading-spinner/LoadingSpinner';

export type GhostButtonSize = 'small' | 'medium';
export type GhostButtonState = 'default' | 'disabled' | 'loading';
export type GhostButtonHierarchy = 'primary' | 'tertiary';
export interface GhostButtonProps extends React.ComponentProps<'button'> {
  /** 버튼 크기 */
  size?: GhostButtonSize;
  /** 버튼 상태 */
  state?: GhostButtonState;
  /** 버튼 계층 */
  hierarchy?: GhostButtonHierarchy;
  /** 버튼 앞쪽 아이콘 */
  leadingNode?: React.ReactNode;
  /** 버튼 앞쪽 아이콘 클릭 이벤트 */
  leadingOnClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  /** 버튼 뒤쪽 아이콘 */
  trailingNode?: React.ReactNode;
  /** 버튼 뒤쪽 아이콘 클릭 이벤트 */
  trailingOnClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  /** 버튼 안의 텍스트 or ReactNode */
  children: React.ReactNode;
}

export default function GhostButton({
  size = 'medium',
  state = 'default',
  hierarchy = 'primary',
  leadingNode,
  leadingOnClick,
  trailingNode,
  trailingOnClick,
  children,
  className,
  ...props
}: GhostButtonProps) {
  const isLoading = state === 'loading';

  return (
    <button
      className={cn(
        'relative flex cursor-pointer items-center justify-center gap-5xs rounded-3xs p-4xs transition-all',
        'hover:bg-comp-ghost-primary-bg-hover',
        'active:bg-comp-ghost-primary-bg-active',
        'disabled:cursor-not-allowed disabled:hover:bg-comp-ghost-primary-bg-disabled',
        isLoading && 'pointer-events-none',
        size === 'medium' && 'min-h-[28px]',
        size === 'small' && 'min-h-[24px]',
        className,
      )}
      data-custom-state={state}
      data-custom-hierarchy={hierarchy}
      disabled={state === 'disabled' || isLoading}
      {...props}
    >
      {leadingNode && !isLoading && (
        <IconWrapper
          size={size}
          hierarchy={hierarchy}
          state={state}
          onClick={leadingOnClick}
        >
          {leadingNode}
        </IconWrapper>
      )}
      {children && (
        <div
          className={cn(
            `${hierarchy === 'primary' && 'text-comp-ghost-primary-text-default'}`,
            `${hierarchy === 'tertiary' && 'text-comp-ghost-tertiary-text-default'}`,
            `${state === 'disabled' && hierarchy === 'primary' && 'text-comp-ghost-primary-text-disabled'}`,
            `${state === 'disabled' && hierarchy === 'tertiary' && 'text-comp-ghost-tertiary-text-disabled'}`,
            'transition-all',
            isLoading && 'opacity-0',
          )}
        >
          <div
            className={cn(
              size === 'medium' && 'text-comp-moderate-medium',
              size === 'small' && 'text-comp-moderate-small',
              isLoading && 'opacity-0',
            )}
          >
            {children}
          </div>
        </div>
      )}
      {trailingNode && !isLoading && (
        <IconWrapper
          size={size}
          hierarchy={hierarchy}
          state={state}
          onClick={trailingOnClick}
        >
          {trailingNode}
        </IconWrapper>
      )}
      <div
        className={cn(
          'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 opacity-0',
          isLoading && 'opacity-100',
        )}
      >
        <LoadingSpinner
          size={size === 'medium' ? 'small' : 'small'}
          className={cn(
            size === 'medium' && 'h-[16px] w-[16px]',
            size === 'small' && 'h-[12px] w-[12px]',
            // hierarchy별 색상 (children 텍스트 색상과 동일)
            hierarchy === 'primary' && 'fill-comp-ghost-primary-text-default',
            hierarchy === 'tertiary' && 'fill-comp-ghost-tertiary-text-default',
            state === 'disabled' &&
              hierarchy === 'primary' &&
              'fill-comp-ghost-primary-text-disabled',
            state === 'disabled' &&
              hierarchy === 'tertiary' &&
              'fill-comp-ghost-tertiary-text-disabled',
          )}
        />
      </div>
    </button>
  );
}

const IconWrapper = ({
  children,
  size,
  hierarchy,
  state,
  onClick,
}: {
  children: ReactNode;
  size: GhostButtonSize;
  hierarchy: GhostButtonHierarchy;
  state: GhostButtonState;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}) => {
  return (
    <div
      className={cn(
        hierarchy === 'primary' &&
          '[&>svg>g>path]:fill-comp-ghost-primary-text-default [&>svg>g>path]:disabled:fill-comp-ghost-primary-text-disabled',
        hierarchy === 'tertiary' &&
          '[&>svg>g>path]:fill-comp-ghost-tertiary-text-default [&>svg>g>path]:disabled:fill-comp-ghost-tertiary-text-disabled',
        `${state === 'disabled' && hierarchy === 'primary' && '[&>svg>g>path]:fill-comp-ghost-primary-text-disabled'}`,
        `${state === 'disabled' && hierarchy === 'tertiary' && '[&>svg>g>path]:fill-comp-ghost-tertiary-text-disabled'}`,
        size === 'medium' && '[&>svg]:h-[20px] [&>svg]:w-[20px]',
        size === 'small' && '[&>svg]:h-[16px] [&>svg]:w-[16px]',
        '[&>svg>g>path]:transition-all',
      )}
      onClick={onClick}
    >
      {children}
    </div>
  );
};
